import { BASE_API, baseReqService } from "../../api";
import { setSessionToken, setSessionRefreshToken, redirectToLogout } from "../authUtil";
import { failedQueue, isOurBackend, isRefreshing, processQueue, refreshToken, setIsRefreshing } from './tokenRefresh';

/**
 * Cookie-based Axios instance for calls that rely on the httpOnly eb_access_token cookie.
 * No Authorization header is added — the browser sends the cookie automatically.
 *
 * Why this exists alongside engagebayTokenAPI: the bootstrap call (get-home-dependencies)
 * must use cookies because Bearer tokens are not yet in localStorage at that point.
 * Post-bootstrap callers also use this instance for same-origin GAE endpoints.
 *
 * 401 handling: silently refreshes via POST /rest/api/login/refresh-token (which also
 * rotates the httpOnly cookie server-side), then retries the original request with the
 * refreshed cookie. Shares isRefreshing + failedQueue with engagebayTokenAPI so a
 * simultaneous cookie-auth and Bearer-auth 401 never triggers two refresh calls.
 *
 * 403 handling: authorization failure (not expiry) — reload immediately.
 */
const engagebaySessionApi = baseReqService.create({ ...BASE_API });

engagebaySessionApi.interceptors.response.use(
    (response) => response,
    async (error) => {
        const status = error?.response?.status;

        if (status === 403) {
            window.location.reload();
            return Promise.reject(error);
        }

        // Only handle 401s from our own backend — external service 401s (OAuth callbacks,
        // third-party APIs) must not trigger a token refresh or session redirect.
        if (status === 401 && !error.config?._retry && isOurBackend(error.config?.url)) {
            error.config._retry = true;

            if (isRefreshing) {
                // Queue behind the already-in-flight refresh
                return new Promise<void>((resolve, reject) => {
                    failedQueue.push({
                        resolve: resolve as (v?: any) => void,
                        reject: reject as (e?: any) => void,
                    });
                })
                    .then(() => engagebaySessionApi(error.config))
                    .catch((e: unknown) => Promise.reject(e));
            }

            setIsRefreshing(true);
            try {
                const data = await refreshToken();
                if (!data?.token) {
                    redirectToLogout();
                    return Promise.reject(error);
                }
                setSessionToken(data.token);
                setSessionRefreshToken(data.refreshToken);
                processQueue(null, data.token);
                // Retry with refreshed cookie — no Authorization header needed
                return engagebaySessionApi(error.config);
            } catch (e: unknown) {
                processQueue(e, null);
                redirectToLogout();
                return Promise.reject(e);
            } finally {
                setIsRefreshing(false);
            }
        }

        return Promise.reject(error);
    }
);

export default engagebaySessionApi;